import { contact } from '../data/site';
import { serviceOptions } from '../data/enquiryOptions';

/**
 * Enquiry delivery endpoint.
 * Create a form at https://formspree.io that delivers to info@liftoffinvestments.co.bw,
 * then paste its endpoint here, e.g. 'https://formspree.io/f/abcdwxyz'.
 * The endpoint ID is public by design; no API keys or mail passwords belong in this file.
 */
export const ENQUIRY_ENDPOINT = '';

export interface EnquiryValues {
  fullName: string;
  organisation: string;
  email: string;
  phone: string;
  service: string;
  location: string;
  budget: string;
  description: string;
  timeline: string;
  consent: boolean;
  /** Honeypot. Must stay empty. */
  website: string;
}

export type EnquiryField = keyof EnquiryValues;
export type EnquiryErrors = Partial<Record<EnquiryField, string>>;

export const emptyEnquiry: EnquiryValues = {
  fullName: '',
  organisation: '',
  email: '',
  phone: '',
  service: '',
  location: '',
  budget: '',
  description: '',
  timeline: '',
  consent: false,
  website: '',
};

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

export function validateField(name: EnquiryField, values: EnquiryValues): string | undefined {
  switch (name) {
    case 'fullName':
      return values.fullName.trim().length < 2 ? 'Enter your full name.' : undefined;
    case 'email':
      if (!values.email.trim()) return 'Enter your email address.';
      return EMAIL_PATTERN.test(values.email.trim()) ? undefined : 'Enter a valid email address, e.g. name@company.co.bw.';
    case 'phone': {
      const digits = values.phone.replace(/\D/g, '');
      if (!values.phone.trim()) return 'Enter a telephone number.';
      return digits.length < 7 || !/^[+()\d\s-]+$/.test(values.phone) ? 'Enter a valid telephone number.' : undefined;
    }
    case 'service':
      return values.service ? undefined : 'Select the service you need.';
    case 'location':
      return values.location.trim().length < 2 ? 'Enter the project location.' : undefined;
    case 'description': {
      const length = values.description.trim().length;
      if (length < 20) return 'Describe the project in at least 20 characters.';
      return length > 3000 ? 'Keep the description under 3,000 characters.' : undefined;
    }
    case 'consent':
      return values.consent ? undefined : 'Please confirm you agree to us using your details to respond.';
    default:
      return undefined;
  }
}

const VALIDATED: EnquiryField[] = ['fullName', 'email', 'phone', 'service', 'location', 'description', 'consent'];

export function validateEnquiry(values: EnquiryValues): EnquiryErrors {
  const errors: EnquiryErrors = {};
  VALIDATED.forEach((field) => {
    const message = validateField(field, values);
    if (message) errors[field] = message;
  });
  return errors;
}

export class EnquiryNotConfiguredError extends Error {
  constructor() {
    super('The online enquiry service has not been connected yet.');
    this.name = 'EnquiryNotConfiguredError';
  }
}

function serviceLabel(value: string) {
  return serviceOptions.find((option) => option.value === value)?.label ?? value;
}

export async function submitEnquiry(values: EnquiryValues, relatedProject?: string) {
  if (!ENQUIRY_ENDPOINT) throw new EnquiryNotConfiguredError();

  const controller = new AbortController();
  const timeout = window.setTimeout(() => controller.abort(), 15000);
  try {
    const response = await fetch(ENQUIRY_ENDPOINT, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      signal: controller.signal,
      body: JSON.stringify({
        _subject: `Website enquiry: ${serviceLabel(values.service)}`,
        _replyto: values.email.trim(),
        _gotcha: values.website,
        name: values.fullName.trim(),
        organisation: values.organisation.trim(),
        email: values.email.trim(),
        phone: values.phone.trim(),
        service: serviceLabel(values.service),
        location: values.location.trim(),
        budget: values.budget,
        timeline: values.timeline,
        relatedProject: relatedProject ?? '',
        description: values.description.trim(),
        consent: 'Yes',
      }),
    });
    if (!response.ok) {
      const data = (await response.json().catch(() => null)) as { errors?: { message?: string }[] } | null;
      throw new Error(data?.errors?.[0]?.message ?? `The enquiry service returned an error (${response.status}).`);
    }
  } catch (error) {
    if (error instanceof DOMException && error.name === 'AbortError') {
      throw new Error('The request timed out. Please check your connection and try again.');
    }
    throw error;
  } finally {
    window.clearTimeout(timeout);
  }
}

export function buildMailtoLink(values: EnquiryValues, relatedProject?: string) {
  const lines = [
    `Name: ${values.fullName}`,
    values.organisation && `Organisation: ${values.organisation}`,
    `Email: ${values.email}`,
    `Telephone: ${values.phone}`,
    `Service: ${serviceLabel(values.service)}`,
    `Location: ${values.location}`,
    values.budget && `Estimated budget: ${values.budget}`,
    values.timeline && `Timeline: ${values.timeline}`,
    relatedProject && `Related project: ${relatedProject}`,
    '',
    values.description,
  ].filter((line) => line !== false && line !== undefined) as string[];
  const subject = `Website enquiry: ${serviceLabel(values.service)}`;
  return `mailto:${contact.email}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(lines.join('\n'))}`;
}

export function enquiryFingerprint(values: EnquiryValues) {
  return [values.email, values.service, values.location, values.description]
    .map((part) => part.trim().toLowerCase())
    .join('|');
}
